/* eslint-disable import/no-unassigned-import */
import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import moment from 'moment';

// Since HC v12, module will not return a factory function, instead, additional modules import as follow and no longer require initialization
import 'highcharts/modules/annotations';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/treemap';
import 'highcharts/modules/wordcloud';
import 'highcharts/modules/sankey';
import 'highcharts/modules/dependency-wheel';
import 'highcharts/modules/timeline';
import 'highcharts/highcharts-more';

import { Constants } from 'app/constants/constants';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

import { TimezoneService } from '../../../services/timezone.service';
import { WidgetChartOptions } from '../components/widget-chart/widget-chart.component';

import { ChartSortHelper } from './chart-sort-helper';

export interface HighchartsOptions {
  [key: string]: any;
}

export interface HighchartsRenderEvent {
  target: Highcharts.Chart;
  type: string;
  preventDefault: () => void;
}

@EnableClassTelemetry()
@Injectable()
export class ChartDisplayService {
  constructor(private timezoneService: TimezoneService) {}

  private applyForPieChart(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ): void {
    const series = [];

    for (let i = 0; i < options.xAxis.categories.length; i++) {
      const value = options.xAxis.categories[i];
      const dataPoint =
        options.series[0]?.data?.find((d) => d.pointId === value || d.name === value) ??
        options.series[0]?.data[i];
      const seriesValue: any = {
        name: value,
        y: dataPoint.y,
        documentCount: dataPoint.documentCount,
        pointId: value,
        id: dataPoint.id ?? value,
        selected: dataPoint.selected,
        sliced: dataPoint.selected,
        color: dataPoint.color,
      };
      if (dataPoint.pointDisplay) {
        seriesValue.pointDisplay = dataPoint.pointDisplay;
      }
      if (dataPoint.custom) {
        seriesValue.custom = dataPoint.custom;
      }
      series.push(seriesValue);
    }
    if (!options.series[0]?.data) {
      options.series[0] = {
        data: [],
      };
    }
    options.series[0].data = series;

    if (options.annotations) {
      const mockAnnotations = options.annotations.filter(
        (a) => typeof a.labels[0].point === 'object'
      );
      for (const annotation of mockAnnotations) {
        annotation.labels[0].point = annotation.custom.target.analyticItems[0].name;
      }
    }
  }

  public getChart(target: HTMLElement): any {
    for (const chart of Highcharts.charts) {
      if (chart && chart.container && chart.container.parentElement === target) {
        return chart;
      }
    }
  }

  public async exportChartAsImage(
    target: HTMLElement,
    title?: string,
    format: 'blob' | 'canvas' = 'blob'
  ): Promise<Blob | HTMLCanvasElement> {
    const chartObject = this.getChart(target);

    const exportingOptions = chartObject.options.exporting;
    if (!exportingOptions.chartOptions) {
      exportingOptions.chartOptions = {};
    }
    exportingOptions.chartOptions.title = { text: title };
    exportingOptions.width = (chartObject.isGantt ? 800 : 600) * exportingOptions.scale;
    exportingOptions.height = 400 * exportingOptions.scale;
    exportingOptions.allowHTML = true;

    const svgObject = chartObject?.getSVGForExport
      ? chartObject?.getSVGForExport(exportingOptions)
      : chartObject.getSVG(exportingOptions);

    const canvas = document.createElement('canvas');
    canvas.width = exportingOptions.width;
    canvas.height = exportingOptions.height;
    const ctx = canvas.getContext('2d');

    return new Promise((resolve) => {
      const img = document.createElement('img');
      img.setAttribute(
        'src',
        'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgObject)))
      );
      img.onload = function () {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        if (format === 'canvas') {
          resolve(canvas);
          return;
        }
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      };
    });
  }

  public drawChart(
    target: HTMLElement,
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions,
    sortable?: boolean
  ): void {
    //We offset the timezone negatively here because for elasticsearch to work it's going to
    //return the correct timestamp for the user's timezone but with the offset at the end (-04:00, +01:00 ,etc.).
    //Hightcharts will see that offset at the end and apply it again to the date bucket.
    // Therefore we need to add the timestamp back here to even it out.
    const timezone = this.timezoneService.timezone;
    options = Object.assign(
      {
        credits: { enabled: false },
        title: { text: '' },
        time: {
          getTimezoneOffset(timestamp) {
            return -moment.tz(timestamp, timezone).utcOffset();
          },
        },
        exporting: {
          buttons: { contextButton: { enabled: false } },
          fallbackToExportServer: false,
        },
      },
      options
    );

    // We have to do this separetly because the options argument can have a chart field
    if (!options.chart?.type) {
      Object.setNestedPropertyValue(options, 'chart.type', widget.payload.graphType);
    }

    if (sortable) {
      const sortHelper = new ChartSortHelper(
        options.series,
        options.xAxis?.categories,
        widget,
        options.xAxis?.type === 'datetime',
        options.isCustomDate,
        options.isCustomNumeric
      );

      const sortedData = sortHelper.sortData();

      if (sortedData?.categories) {
        options.xAxis.categories = sortedData.categories;
      }
      if (sortedData?.series) {
        sortedData?.series.forEach((s, i) => {
          options.series[i].data = s;
        });
      }
    }

    if (
      widget.payload.legends &&
      widget.payload.type !== 'sentimentPolarity' &&
      widget.payload.type !== 'sentimentVolume'
    ) {
      for (const serie of options.series) {
        serie.visible = widget.payload.legends[serie.name] ?? true;
      }
    }

    if (
      !['pie', 'wordcloud', 'crosstab', 'dependencywheel', 'gauge'].includes(
        widget.payload.graphType
      )
    ) {
      options.chart.events ??= {};
      options.chart.events.load = (event) => {
        if (!event.target.series.length || !event.target.yAxis[0]?.paddedTicks) {
          return;
        }

        const numDec = function (n) {
          const strN = '' + n;
          return n % 1 === 0 ? 0 : strN.length - strN.lastIndexOf('.') - 1;
        };

        let maxDecimals = 0;
        for (const tick of event.target.yAxis[0].paddedTicks) {
          maxDecimals = Math.max(maxDecimals, numDec(tick));
        }

        event.target.series[0].options = event.target.series[0].options || {};
        event.target.series[0].options.maxDecimals = maxDecimals + 1;
      };
    }

    if (widget.payload.graphType === 'pie') {
      this.applyForPieChart(dashboard, widget, response, options);
    }

    //DASH-4699
    options.chart.resetZoomButton = {
      theme: {
        style: {
          display: 'none',
        },
      },
    };

    if (options.annotations) {
      options.annotations.sort((a1, a2) => {
        if (a1.visible) {
          if (a2.visible) {
            return 0;
          }
          return -1;
        }

        if (a2.visible) {
          return 1;
        }

        return 0;
      });
    }

    if (options.tooltip) {
      options.tooltip.outside = true;
    }

    Highcharts.chart(target, options);
  }

  public reflowChart(target: HTMLElement): void {
    const chart = this.getChart(target);
    if (chart) {
      chart.reflow();
      chart.legend.update();
    }
  }

  public sortChart(widget, target: HTMLElement, isCustomDate, isCustomNumeric): any[] {
    const chart = this.getChart(target);
    if (chart) {
      const sortHelper = new ChartSortHelper(
        chart.series,
        chart.xAxis[0]?.categories,
        widget,
        chart.xAxis[0]?.options.type === 'datetime',
        isCustomDate,
        isCustomNumeric
      );

      let sortedData: any = sortHelper.sortData();

      chart.series.forEach((serie, i) => {
        serie.isDirty = true;
        if (sortedData?.series) {
          serie.updateData(sortedData.series[i], false);
          serie.data.sort((d1, d2) => d1.x - d2.x);
        }
      });

      if (sortedData?.categories) {
        chart.xAxis[0].setCategories(sortedData.categories, false);
        // we need to represent the changed data in userOptions.series[0].data for image export reasons
        // don't ask me why does the chart uses that property for creating the svg element
        chart.userOptions.series[0].data = this.sorter(chart.userOptions.series[0].data, chart);
      } else {
        sortedData = { categories: chart.series.map((s) => s.name) };
      }

      chart.legend.update({}, false);

      chart.redraw(true);

      return sortedData?.categories;
    }

    return [];
  }

  /**
   * Sorts an array of data points according to the order of their pointId in the first series of the chart.
   * Also adds the x value from the first series to each data point.
   * @param array data points to sort
   * @param chart the chart to get the order of pointId from
   * @returns sorted array of data points
   */
  private sorter(array: Array<any>, chart) {
    return array
      .sort((d1, d2) => {
        return (
          chart.series[0].data.findIndex(
            (chartSeriesData) => chartSeriesData.pointId === d1.pointId
          ) -
          chart.series[0].data.findIndex(
            (chartSeriesData) => chartSeriesData.pointId === d2.pointId
          )
        );
      })
      .map((res) => ({
        ...res,
        x: chart.series[0].data.find((chartSerData) => chartSerData.pointId === res.pointId).x,
      }));
  }

  public getChartItems(target: HTMLElement): { name; label }[] {
    const chart = this.getChart(target);
    if (chart) {
      if (chart.xAxis[0]?.options.type === 'datetime') {
        return chart.series.map((s) => ({
          name: s.custom?.seriesId ?? s.name,
          label: Utils.unescapeHtml(s.name || Constants.BLANK),
        }));
      }

      return chart.series[0].data.map((p) => ({
        name: p.pointId || p.category,
        label: Utils.unescapeHtml((p.pointDisplay ?? p.pointId ?? p.category) || Constants.BLANK),
      }));
    }

    return [];
  }

  getChartColors(target: HTMLElement) {
    const chart = this.getChart(target);
    if (chart) {
      return {
        itemColors: chart.series[0].data.map((d) => d.color),
        seriesColors: chart.series.map((s) => s.color),
      };
    }
  }
}
